import { useEffect, useState } from 'react';
import { IconEdit, PermissionEnum, SelectType, formatDate } from '@ui/src';
import { useAppSelector } from '@store';
import Card from '../../partial/card/card';
import OverviewGeneralRow from './general-row';
import { CustomerStatus } from '../../../hoc/customers/types';
import { CustomerOverviewGeneralTypes } from '../types';

import cn from './overview.module.css';

const CustomerOverviewGeneral = ({
  isLoading,
  general,
  control,
  businessAddressSameAsRegisteredAddress,
  correspondenceAddressSameAsRegisteredAddress,
  onSubmit,
  requestInProgress,
  isSuccess,
  errors,
  globalError,
  status,
}: CustomerOverviewGeneralTypes) => {
  const [edit, setEdit] = useState(false);

  const {
    countries,
    incorporationDates,
    activePermissions,
    sicCodesDictionaries,
    productsCorporate,
  } = useAppSelector((state) => state.customersDictionaries);

  let correspondenceContent;
  let businessContent;
  let registeredContent;

  useEffect(() => {
    if (isSuccess) {
      setEdit(false);
    }
  }, [isSuccess]);

  if (!edit) {
    registeredContent = (
      <OverviewGeneralRow
        title="Registration address"
        description={general?.registrationAddress}
        control={control}
        edit={edit}
        type="text"
      />
    );
  } else {
    registeredContent = (
      <>
        <OverviewGeneralRow
          title="Registration country"
          control={control}
          edit={edit}
          name="registeredCountryId"
          type="autocomplete"
          list={countries}
          error={errors?.registeredCountryId?.message}
        />
        <OverviewGeneralRow
          title="Registration city"
          control={control}
          edit={edit}
          name="registeredCity"
          type="text"
          error={errors?.registeredCity?.message}
        />
        <OverviewGeneralRow
          title="Registration postal code"
          control={control}
          edit={edit}
          name="registeredPostalCode"
          type="text"
          error={errors?.registeredPostalCode?.message}
        />
        <OverviewGeneralRow
          title="Registration address 1"
          control={control}
          edit={edit}
          name="registeredAddress1"
          type="text"
          error={errors?.registeredAddress1?.message}
        />
        <OverviewGeneralRow
          title="Registration address 2"
          control={control}
          edit={edit}
          name="registeredAddress2"
          error={errors?.registeredAddress2?.message}
          type="text"
        />
      </>
    );
  }

  if (!edit) {
    correspondenceContent = (
      <OverviewGeneralRow
        title="Correspondent address"
        description={general?.correspondentAddress}
        control={control}
      />
    );
  } else {
    correspondenceContent = (
      <>
        <OverviewGeneralRow
          title="Correspondent address"
          control={control}
          edit={edit}
          name="correspondenceAddressSameAsRegisteredAddress"
          checkboxTitle="Coincides with the registered address"
          type="checkbox"
        />
        {!correspondenceAddressSameAsRegisteredAddress && (
          <>
            <OverviewGeneralRow
              title="Correspondent country"
              control={control}
              edit={edit}
              name="correspondenceCountryId"
              type="autocomplete"
              list={countries}
              error={errors?.correspondenceCountryId?.message}
            />
            <OverviewGeneralRow
              title="Correspondent city"
              control={control}
              edit={edit}
              name="correspondenceCity"
              type="text"
              error={errors?.correspondenceCity?.message}
            />
            <OverviewGeneralRow
              title="Correspondent postal code"
              control={control}
              edit={edit}
              name="correspondencePostalCode"
              type="text"
              error={errors?.correspondencePostalCode?.message}
            />
            <OverviewGeneralRow
              title="Correspondent address 1"
              control={control}
              edit={edit}
              name="correspondenceAddress1"
              type="text"
              error={errors?.correspondenceAddress1?.message}
            />
            <OverviewGeneralRow
              title="Correspondent address 2"
              control={control}
              edit={edit}
              name="correspondenceAddress2"
              type="text"
              error={errors?.correspondenceAddress2?.message}
            />
          </>
        )}
      </>
    );
  }

  if (!edit) {
    businessContent = (
      <OverviewGeneralRow
        title="Business address"
        description={general?.businessAddress}
        control={control}
      />
    );
  } else {
    businessContent = (
      <>
        <OverviewGeneralRow
          title="Business address"
          control={control}
          edit={edit}
          name="businessAddressSameAsRegisteredAddress"
          checkboxTitle="Coincides with the registered address"
          type="checkbox"
        />
        {!businessAddressSameAsRegisteredAddress && (
          <>
            <OverviewGeneralRow
              title="Business country"
              control={control}
              edit={edit}
              name="businessCountryId"
              type="autocomplete"
              list={countries}
              error={errors?.businessCountryId?.message}
            />
            <OverviewGeneralRow
              title="Business city"
              control={control}
              edit={edit}
              name="businessCity"
              type="text"
              error={errors?.businessCity?.message}
            />
            <OverviewGeneralRow
              title="Business postal code"
              control={control}
              edit={edit}
              name="businessPostalCode"
              type="text"
              error={errors?.businessPostalCode?.message}
            />
            <OverviewGeneralRow
              title="Business address 1"
              control={control}
              edit={edit}
              name="businessAddress1"
              type="text"
              error={errors?.businessAddress1?.message}
            />
            <OverviewGeneralRow
              title="Business address 2"
              control={control}
              edit={edit}
              name="businessAddress2"
              type="text"
              error={errors?.businessAddress2?.message}
            />
          </>
        )}
      </>
    );
  }

  return (
    <Card title="General Info" isLoading={isLoading}>
      <div className={cn.generalTop}>
        <div className={cn.generalTopLeft}>
          {edit && (
            <OverviewGeneralRow
              title="Company Name"
              control={control}
              edit={edit}
              name="companyName"
              type="text"
              error={errors?.companyName?.message}
            />
          )}
          <OverviewGeneralRow
            title="Registered number"
            description={general?.companyNumber?.toUpperCase()}
            edit={edit}
            control={control}
            name="companyNumber"
            type="text"
            error={errors?.companyNumber?.message}
          />
          {/* {!edit && (
            <OverviewGeneralRow
              title="Entity form"
              description={general?.entityForm}
              control={control}
            />
          )} */}
          <OverviewGeneralRow
            title="Incorporated on"
            description={
              incorporationDates?.find(
                (item) => item.value === general?.dateOfIncorporation,
              )?.name || formatDate(general?.dateOfIncorporation as string)
            }
            control={control}
            edit={edit}
            name="dateOfIncorporation"
            type="date"
            error={errors?.dateOfIncorporation?.message}
          />
          <OverviewGeneralRow
            title="Country of incorporation"
            description={
              countries?.find(
                (item) => item.value === general?.countryOfIncorporationId,
              )?.name
            }
            control={control}
            edit={edit}
            name="countryOfIncorporationId"
            type="autocomplete"
            list={countries}
            error={errors?.countryOfIncorporationId?.message}
          />
          <OverviewGeneralRow
            title="Merchant ID"
            description={general?.merchantId || '-'}
            edit={edit}
            control={control}
            name="merchantId"
            type="text"
            error={errors?.merchantId?.message}
          />
          <OverviewGeneralRow
            title="SIC code"
            description={general?.sicCodes.join(',')}
            control={control}
            edit={edit}
            name="sicCodes"
            type="autocomplete-multiple"
            list={sicCodesDictionaries?.map((item) => ({
              ...item,
              value: item.name,
            }))}
            error={errors?.sicCodes?.message}
            zIndex={6}
          />
          <OverviewGeneralRow
            title="Products"
            control={control}
            type="select"
            name="productIds"
            edit={edit}
            selectType={SelectType.MULTIPLE}
            defaultValue={productsCorporate
              ?.filter((item) => {
                let result;

                if (
                  general?.productIds?.find((product) => product === item.value)
                ) {
                  result = item;
                }

                return result;
              })
              .map((item) => item.name)
              .join(', ')}
            list={productsCorporate}
            error={errors?.productIds?.message}
            zIndex={5}
          />
          {/* <OverviewGeneralRow
            title="MCC code"
            description={general?.mccCode}
            control={control}
            edit={edit}
            name="mccCode"
            type="text"
            error={errors?.mccCode?.message}
          /> */}
          {edit && correspondenceContent}
        </div>
        <div className={cn.generalTopRight}>
          {!edit && (
            <OverviewGeneralRow
              title="Status"
              description={general?.status}
              control={control}
            />
          )}
          {!edit && correspondenceContent}
          {registeredContent}
          {businessContent}
          <OverviewGeneralRow
            title="Site"
            edit={edit}
            control={control}
            type="text"
            name="websiteLink"
            error={errors?.websiteLink?.message}
          >
            <a
              href={`${
                general?.websiteLink?.includes('http') ||
                general?.websiteLink?.includes('https')
                  ? ''
                  : 'https://'
              }${general?.websiteLink}`}
              target="_blank"
              rel="noopener noreferrer"
              className={cn.generalSite}
            >
              {general?.websiteLink}
            </a>
          </OverviewGeneralRow>
          <OverviewGeneralRow
            title="VAT Number"
            edit={edit}
            control={control}
            description={general?.vatNumber || '-'}
            type="text"
            name="vatNumber"
            error={errors?.vatNumber?.message}
          />
        </div>
      </div>
      <div className={cn.generalBottom}>
        <OverviewGeneralRow
          title="Business description"
          description={general?.businessDescription}
          edit={edit}
          control={control}
          type="textarea"
          name="businessDescription"
          error={errors?.businessDescription?.message}
        />
      </div>
      <div className={cn.editControls}>
        {globalError && <div className={cn.globalError}>{globalError}</div>}
        {edit && (
          <>
            <button
              type="submit"
              className={cn.saveButton}
              aria-label="Edit Button"
              onClick={onSubmit}
              disabled={requestInProgress}
            >
              {requestInProgress ? 'Loading...' : 'Save'}
            </button>
            <button
              type="button"
              className={cn.cancelButton}
              aria-label="Cance Button"
              onClick={() => setEdit(false)}
            >
              Cancel
            </button>
          </>
        )}
        {!edit &&
          status !== CustomerStatus.CLOSED &&
          activePermissions?.includes(
            PermissionEnum.CORPORATE_CUSTOMERS_EDIT_DETAILS_ACTION,
          ) && (
            <button
              type="button"
              className={cn.editButton}
              aria-label="Edit Button"
              onClick={() => setEdit(true)}
            >
              <IconEdit /> Edit
            </button>
          )}
      </div>
    </Card>
  );
};

export default CustomerOverviewGeneral;
